import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Tooltip, theme } from 'antd';
import {
  AppstoreOutlined,
  BellOutlined,
  DashboardOutlined,
  IdcardOutlined,
  ArrowRightOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MessageOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { ROUTES } from '@shared/config/routes';
import { useLogout } from '@features/auth';
import { useCurrentUserQuery } from '@entities/session';
import { useUnreadNotificationsCount } from '@entities/notification';
import { ThemeToggle } from '@widgets/theme-toggle';
import { KycBanner } from '@widgets/kyc-banner';
import { GlobalInbox } from '@widgets/global-inbox';

const { Sider, Header, Content } = Layout;

type NavItem = {
  key: string;
  labelKey: string;
  icon: React.ReactNode;
};

const NAV: NavItem[] = [
  { key: ROUTES.root, labelKey: 'nav.dashboard', icon: <DashboardOutlined /> },
  { key: ROUTES.profile, labelKey: 'nav.profile', icon: <IdcardOutlined /> },
  { key: ROUTES.products, labelKey: 'nav.catalog', icon: <AppstoreOutlined /> },
  { key: ROUTES.buyRequests, labelKey: 'nav.buyRequests', icon: <ShopOutlined /> },
  { key: ROUTES.quotes, labelKey: 'nav.myQuotes', icon: <UnorderedListOutlined /> },
  { key: ROUTES.orders, labelKey: 'nav.orders', icon: <ShoppingCartOutlined /> },
  { key: ROUTES.chats, labelKey: 'nav.chats', icon: <MessageOutlined /> },
  { key: ROUTES.notifications, labelKey: 'nav.notifications', icon: <BellOutlined /> },
  { key: ROUTES.settings, labelKey: 'nav.settings', icon: <SettingOutlined /> },
];

function pickActiveKey(pathname: string): string {
  const direct = NAV.find((n) => n.key === pathname);
  if (direct) return direct.key;
  const prefixMatches = NAV.filter(
    (n) => n.key !== '/' && pathname.startsWith(n.key + '/'),
  ).sort((a, b) => b.key.length - a.key.length);
  return prefixMatches[0]?.key ?? '/';
}

/** Brand card pinned to the bottom of the sider. */
function SiderPromo() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <div
      className="brand-gradient"
      style={{
        borderRadius: 14,
        padding: 14,
        border: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <span className="brand-mark" style={{ fontWeight: 700, fontSize: 15, letterSpacing: 0.6 }}>
        AIDU
      </span>
      <span style={{ fontSize: 12, lineHeight: 1.45, color: token.colorTextSecondary }}>
        {t('nav.promo')}
      </span>
      <span
        aria-hidden
        className="brand-chip"
        style={{
          alignSelf: 'flex-end',
          width: 28,
          height: 28,
          borderRadius: 9,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 12,
        }}
      >
        <ArrowRightOutlined />
      </span>
    </div>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const [collapsed, setCollapsed] = useState(false);
  const { token } = theme.useToken();
  const { data: user } = useCurrentUserQuery();
  const { data: unread } = useUnreadNotificationsCount();
  const activeKey = useMemo(() => pickActiveKey(location.pathname), [location.pathname]);

  const userMenu = {
    items: [
      { key: 'settings', icon: <SettingOutlined />, label: t('common.settings') },
      { type: 'divider' as const },
      { key: 'logout', icon: <LogoutOutlined />, label: t('common.logout'), danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'settings') navigate(ROUTES.settings);
      if (key === 'logout') void logout();
    },
  };

  const userInitial = (user?.email ?? 'AC').slice(0, 2).toUpperCase();

  return (
    <Layout className="app-ambient" style={{ minHeight: '100vh', background: 'transparent' }}>
      <GlobalInbox />
      <Sider
        className="app-sider"
        width={248}
        breakpoint="lg"
        collapsedWidth={72}
        collapsible
        collapsed={collapsed}
        trigger={null}
        onBreakpoint={setCollapsed}
        style={{ borderRight: `1px solid ${token.colorBorderSecondary}` }}
      >
        {/* Full-height column so the promo card can sit at the bottom. */}
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'space-between',
              gap: 10,
              height: 64,
              paddingInline: collapsed ? 0 : 16,
              flexShrink: 0,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span
                aria-hidden
                className="brand-chip"
                style={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: 9,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                A
              </span>
              {collapsed ? null : (
                <span
                  className="brand-mark"
                  style={{ fontWeight: 700, fontSize: 19, letterSpacing: 1.5 }}
                >
                  AIDU
                </span>
              )}
            </span>
            {collapsed ? null : (
              <Button
                type="text"
                size="small"
                aria-label={t('nav.collapse')}
                onClick={() => setCollapsed(true)}
                icon={<MenuFoldOutlined style={{ color: token.colorTextTertiary }} />}
              />
            )}
          </div>

          {collapsed ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 4 }}>
              <Button
                type="text"
                size="small"
                aria-label={t('nav.expand')}
                onClick={() => setCollapsed(false)}
                icon={<MenuUnfoldOutlined style={{ color: token.colorTextTertiary }} />}
              />
            </div>
          ) : null}

          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={[activeKey]}
            onClick={({ key }) => navigate(key)}
            items={NAV.map((n) => ({ key: n.key, icon: n.icon, label: t(n.labelKey) }))}
            style={{ borderInlineEnd: 'none', paddingInline: 8, flex: 1, overflowY: 'auto' }}
          />

          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <KycBanner />
            {collapsed ? null : <SiderPromo />}
          </div>
        </div>
      </Sider>
      <Layout>
        <Header
          className="app-header-glass"
          style={{
            paddingInline: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
          }}
        >
          <Space size={12}>
            <ThemeToggle />
            <Tooltip title={t('nav.notifications')}>
              <Button
                type="text"
                onClick={() => navigate(ROUTES.notifications)}
                icon={
                  <BellOutlined
                    style={{
                      color: unread?.count ? token.colorError : token.colorTextSecondary,
                    }}
                  />
                }
              >
                {unread?.count ? unread.count : null}
              </Button>
            </Tooltip>
            <Dropdown menu={userMenu} placement="bottomRight" arrow>
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size={32}
                  className="brand-chip"
                  style={{ fontWeight: 600, border: 'none' }}
                >
                  {userInitial}
                </Avatar>
                <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                  {user?.email ?? '—'}
                </span>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ padding: 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
